/**
 * INVOICE EMAIL NOTIFICATION SERVICE
 * 
 * Handles sending email notifications for invoice-related events:
 * - Invoice created
 * - Invoice approved
 * - Invoice rejected
 * - Invoice paid
 * - Email invoice PDF to user
 */

export interface InvoiceEmailData {
 invoiceNumber: string;
 userName: string;
 userEmail: string;
 totalAmount: number;
 currency: string;
 companyEntity: 'SUN' | 'Aureus';
 status: string;
 rejectionReason?: string;
 paymentReference?: string;
 invoiceDate?: string;
 dueDate?: string;
}

// Company branding
const COMPANY_DETAILS = {
 SUN: {
 name: 'SUN Alliance Holdings (Pty) Ltd',
 color: '#FFC107',
 logo: ''
 },
 Aureus: {
 name: 'Aureus Alliance Holdings (Pty) Ltd',
 color: '#D4AF37',
 logo: ''
 }
};

/**
 * Format currency for display
 */
const formatCurrency = (amount: number, currency: string = 'USD'): string => {
 const symbols: Record<string, string> = {
 USD: '$',
 ZAR: 'R',
 EUR: '€',
 GBP: '£'
 };
 const symbol = symbols[currency] || '$';
 return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Generate base email template
 */
const generateEmailTemplate = (title: string, content: string, company: 'SUN' | 'Aureus'): string => {
 const companyInfo = COMPANY_DETAILS[company];
 return `
<!DOCTYPE html>
<html>
<head>
 <meta charset="utf-8">
 <meta name="viewport" content="width=device-width, initial-scale=1.0">
 <title>${title}</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #1F2937;">
 <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
 <div style="background: linear-gradient(135deg, #111827 0%, #1F2937 100%); border-radius: 12px; padding: 30px; border: 1px solid #374151;">
 <!-- Header -->
 <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid ${companyInfo.color}; padding-bottom: 20px;">
 <div style="font-size: 36px; margin-bottom: 10px;">${companyInfo.logo}</div>
 <h2 style="color: ${companyInfo.color}; margin: 0; font-size: 20px;">${companyInfo.name}</h2>
 </div>
 
 <!-- Title -->
 <h1 style="color: #F3F4F6; text-align: center; margin-bottom: 25px; font-size: 24px;">${title}</h1>
 
 <!-- Content -->
 <div style="color: #D1D5DB; font-size: 15px; line-height: 1.6;">
 ${content}
 </div>
 
 <!-- Footer -->
 <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #374151; text-align: center;">
 <p style="color: #6B7280; font-size: 12px; margin: 0;">
 This email was sent by ${companyInfo.name}<br>
 For questions, contact support@aureus.africa
 </p>
 </div>
 </div>
 </div>
</body>
</html>`;
};

/**
 * Send invoice notification email
 */
export async function sendInvoiceNotificationEmail(
 type: 'created' | 'approved' | 'rejected' | 'paid',
 data: InvoiceEmailData
): Promise<{ success: boolean; error?: string }> {
 try {
 const { invoiceNumber, userName, userEmail, totalAmount, currency, companyEntity, rejectionReason, paymentReference } = data;
 
 let title = '';
 let content = '';
 
 switch (type) {
 case 'created':
 title = ' New Invoice Created';
 content = `
 <p>Hello <strong>${userName}</strong>,</p>
 <p>A new invoice has been created for your account:</p>
 <div style="background-color: #111827; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Invoice Number:</strong> <span style="color: #10B981;">${invoiceNumber}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Amount:</strong> <span style="color: #10B981; font-size: 18px;">${formatCurrency(totalAmount, currency)}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Status:</strong> <span style="color: #F59E0B;">Pending Approval</span></p>
 </div>
 <p>The invoice is currently pending approval. You will be notified once it has been reviewed.</p>
 `;
 break;
 
 case 'approved':
 title = ' Invoice Approved';
 content = `
 <p>Hello <strong>${userName}</strong>,</p>
 <p>Great news! Your invoice has been approved:</p>
 <div style="background-color: #111827; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Invoice Number:</strong> <span style="color: #10B981;">${invoiceNumber}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Amount:</strong> <span style="color: #10B981; font-size: 18px;">${formatCurrency(totalAmount, currency)}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Status:</strong> <span style="color: #22C55E;">Approved</span></p>
 </div>
 <p>You can now request payment for this invoice from your dashboard.</p>
 `;
 break;
 
 case 'rejected':
 title = ' Invoice Rejected';
 content = `
 <p>Hello <strong>${userName}</strong>,</p>
 <p>Unfortunately, your invoice has been rejected:</p>
 <div style="background-color: #111827; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Invoice Number:</strong> <span style="color: #EF4444;">${invoiceNumber}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Amount:</strong> <span style="color: #F3F4F6;">${formatCurrency(totalAmount, currency)}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Status:</strong> <span style="color: #EF4444;">Rejected</span></p>
 ${rejectionReason ? `<p style="margin: 10px 0 5px 0;"><strong style="color: #9CA3AF;">Reason:</strong></p><p style="color: #FCA5A5; margin: 0; padding: 10px; background-color: #7F1D1D; border-radius: 4px;">${rejectionReason}</p>` : ''}
 </div>
 <p>If you have questions about this decision, please contact support.</p>
 `;
 break;
 
 case 'paid':
 title = ' Invoice Payment Processed';
 content = `
 <p>Hello <strong>${userName}</strong>,</p>
 <p>Your invoice payment has been processed:</p>
 <div style="background-color: #111827; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Invoice Number:</strong> <span style="color: #10B981;">${invoiceNumber}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Amount Paid:</strong> <span style="color: #10B981; font-size: 18px;">${formatCurrency(totalAmount, currency)}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Status:</strong> <span style="color: #22C55E;">Paid </span></p>
 ${paymentReference ? `<p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Reference:</strong> <span style="color: #F3F4F6;">${paymentReference}</span></p>` : ''}
 </div>
 <p>Thank you for your business!</p>
 `;
 break;
 }

 const htmlContent = generateEmailTemplate(title, content, companyEntity);
 
 // Send via API
 const response = await fetch('/api/send-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 emailType: 'custom',
 data: {
 email: userEmail,
 subject: `${title} - ${invoiceNumber}`,
 htmlContent,
 textContent: content.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim(),
 category: `invoice_${type}`
 }
 })
 });

 const result = await response.json();
 
 if (!response.ok || !result.success) {
 console.error('Failed to send invoice email:', result.error);
 return { success: false, error: result.error || 'Failed to send email' };
 }

 console.log(`Invoice ${type} email sent to ${userEmail}`);
 return { success: true };

 } catch (error) {
 console.error('Invoice email error:', error);
 return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
 }
}

/**
 * Send invoice copy email (with download link)
 */
export async function sendInvoiceCopyEmail(
 data: InvoiceEmailData & { downloadUrl?: string }
): Promise<{ success: boolean; error?: string }> {
 try {
 const { invoiceNumber, userName, userEmail, totalAmount, currency, companyEntity, status, invoiceDate, dueDate } = data;
 const companyInfo = COMPANY_DETAILS[companyEntity];

 const title = ' Your Invoice Copy';
 const content = `
 <p>Hello <strong>${userName}</strong>,</p>
 <p>As requested, here is a copy of your invoice:</p>
 <div style="background-color: #111827; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Invoice Number:</strong> <span style="color: #10B981;">${invoiceNumber}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Amount:</strong> <span style="color: #10B981; font-size: 18px;">${formatCurrency(totalAmount, currency)}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Status:</strong> <span style="color: ${status === 'paid' ? '#22C55E' : '#F59E0B'};">${status.replace('_', ' ').toUpperCase()}</span></p>
 ${invoiceDate ? `<p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Invoice Date:</strong> <span style="color: #F3F4F6;">${invoiceDate}</span></p>` : ''}
 ${dueDate ? `<p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Due Date:</strong> <span style="color: #F3F4F6;">${dueDate}</span></p>` : ''}
 </div>
 <p>You can download the PDF version of your invoice from your dashboard.</p>
 <p style="margin-top: 20px;">If you have any questions about this invoice, please don't hesitate to contact us.</p>
 `;

 const htmlContent = generateEmailTemplate(title, content, companyEntity);

 const response = await fetch('/api/send-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 emailType: 'custom',
 data: {
 email: userEmail,
 subject: `Invoice Copy: ${invoiceNumber} - ${companyInfo.name}`,
 htmlContent,
 textContent: content.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim(),
 category: 'invoice_copy'
 }
 })
 });

 const result = await response.json();

 if (!response.ok || !result.success) {
 console.error('Failed to send invoice copy email:', result.error);
 return { success: false, error: result.error || 'Failed to send email' };
 }

 console.log(`Invoice copy email sent to ${userEmail}`);
 return { success: true };

 } catch (error) {
 console.error('Invoice copy email error:', error);
 return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
 }
}

